/**
 * The entry points that nest one component under another, take one out, and put a dissolved
 * group back.
 *
 * Apart from the rest because these answer the screen: a drop can be refused as full, and a
 * drop or a take-out can dissolve a group, which the screen says in a toast with an Undo.
 * Every import from the hook is type-only, so nothing is loaded back from the module that
 * calls it.
 */

import type { Definitions, ElementRow } from "@/lib/element-registry";
import {
  dissolvedBetween,
  nest,
  takeOut,
  type DissolvedGroup,
  type UndoneDrop,
} from "@/lib/figure-registry";
import type { DefinitionChange } from "./use-figure-definitions";

/** What a nesting edit came to, read against the set on screen when it was asked for. */
export type NestOutcome =
  | { kind: "full" }
  | { kind: "unchanged" }
  | { kind: "nested"; dissolved: DissolvedGroup[] };

export function nestingEntryPoints(
  issue: (change: DefinitionChange) => void,
  showing: () => Definitions,
) {
  return {
    /** Drop one component on another; the group id is minted here so the change carries it. */
    nestComponent: (dragged: string, target: string): NestOutcome => {
      const before = showing().registry;
      const groupId = crypto.randomUUID();
      const after = nest(before, { dragged, target, groupId });
      if (after === "full") return { kind: "full" };
      if (after === before) return { kind: "unchanged" };
      issue({ kind: "nest", dragged, target, groupId });
      // Its Undo reverses the whole drop, so it names the group the dragged one joined.
      const joined = after.find((r) => r.element_id === dragged)?.group_id;
      return {
        kind: "nested",
        dissolved: dissolvedBetween(before, after).map((gone) =>
          joined
            ? { ...gone, undoing: { elementId: dragged, from: joined } }
            : gone,
        ),
      };
    },

    takeOutComponent: (elementId: string): NestOutcome => {
      const before = showing().registry;
      const after = takeOut(before, elementId);
      if (after === before) return { kind: "unchanged" };
      issue({ kind: "take-out", elementId });
      return { kind: "nested", dissolved: dissolvedBetween(before, after) };
    },

    restoreGroup: (
      group: ElementRow,
      memberIds: string[],
      undoing?: UndoneDrop,
    ) => issue({ kind: "restore-group", group, memberIds, undoing }),
  };
}
